/**
 * OperationRunDialog — Integration Hub
 * -----------------------------------------------------------------------------
 * Modal "Nova consulta": monta o formulário dinamicamente a partir do
 * `params_schema` do recurso (nada é hardcoded por endpoint), executa a
 * chamada via `integration-gateway` e devolve o resultado ao chamador.
 *
 * Segurança/validação:
 *   - Campos obrigatórios validados antes do envio (evita gastar saldo à toa).
 *   - Documento (CPF/CNPJ/placa) e UF validados no cliente; o backend revalida.
 *
 * @version 1.1.0
 * RELEASE: 2026-08-29 v1.1.0 - Consulta passa a ser enfileirada (fila q_integration):
 *          o modal fecha imediatamente e o resultado aparece no histórico.
 * RELEASE: 2026-08-29 v1.0.0 - Criação inicial (botão Consultar na aba Recursos).
 */

import { useEffect, useMemo, useState } from 'react';
import { Loader2, Search } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useEnqueueIntegrationOperation } from '@/hooks/integrations/useIntegrationConsultations';
import type {
  IntegrationConnection,
  IntegrationOperation,
} from '@/hooks/integrations/useIntegrationConnections';

/** Campo declarado no params_schema da operação */
interface ParamField {
  key: string;
  type?: string;
  label?: string;
  required?: boolean;
  help?: string;
  default?: unknown;
}

interface OperationRunDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Recurso a ser consultado */
  operation: IntegrationOperation | null;
  /** Conexões ativas disponíveis para este provider */
  connections: IntegrationConnection[];
  /** Conexão pré-selecionada */
  defaultConnectionId?: string | null;
  /** Valores iniciais (usado no "Reexecutar" a partir do histórico) */
  initialValues?: Record<string, unknown>;
  /** Chamado quando a consulta é aceita e entra na fila de processamento */
  onQueued: (info: { consultationId: string; operation: IntegrationOperation }) => void;
}

/** Extrai os campos do params_schema (aceita array ou objeto vazio) */
function readFields(operation: IntegrationOperation | null): ParamField[] {
  const schema = (operation as { params_schema?: unknown } | null)?.params_schema;
  if (!Array.isArray(schema)) return [];
  return schema.filter((f): f is ParamField => Boolean(f && typeof f === 'object' && 'key' in f));
}

/** Validação de documento aceita pela API (CPF, CNPJ ou placa) */
function isValidDocument(value: string): boolean {
  const digits = value.replace(/\D/g, '');
  if (digits.length === 11 || digits.length === 14) return true;
  const plate = value.replace(/[^A-Za-z0-9]/g, '').toUpperCase();
  return /^[A-Z]{3}[0-9][A-Z0-9][0-9]{2}$/.test(plate);
}

export function OperationRunDialog({
  open,
  onOpenChange,
  operation,
  connections,
  defaultConnectionId,
  initialValues,
  onQueued,
}: OperationRunDialogProps) {
  const fields = useMemo(() => readFields(operation), [operation]);
  const activeConnections = useMemo(
    () => connections.filter((c) => c.is_active),
    [connections],
  );

  const [connectionId, setConnectionId] = useState<string>('');
  const [values, setValues] = useState<Record<string, unknown>>({});
  const [errors, setErrors] = useState<Record<string, string>>({});

  const runOperation = useEnqueueIntegrationOperation();

  /** Reinicializa o formulário sempre que o modal abre em outro recurso */
  useEffect(() => {
    if (!open) return;

    const initial: Record<string, unknown> = {};
    fields.forEach((field) => {
      const fromHistory = initialValues?.[field.key];
      initial[field.key] =
        fromHistory !== undefined
          ? fromHistory
          : field.default !== undefined
            ? field.default
            : field.type === 'boolean'
              ? false
              : '';
    });

    setValues(initial);
    setErrors({});
    setConnectionId(
      defaultConnectionId ??
        activeConnections.find((c) => c.is_default)?.id ??
        activeConnections[0]?.id ??
        '',
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, operation?.code]);

  /** Valida e dispara a consulta */
  const handleRun = async () => {
    if (!operation || !connectionId) return;

    const nextErrors: Record<string, string> = {};
    fields.forEach((field) => {
      const value = values[field.key];
      const text = typeof value === 'string' ? value.trim() : value;

      if (field.required && (text === '' || text === undefined || text === null)) {
        nextErrors[field.key] = 'Campo obrigatório';
        return;
      }
      if (field.key === 'document' && typeof text === 'string' && text && !isValidDocument(text)) {
        nextErrors[field.key] = 'Informe um CPF, CNPJ ou placa válido';
      }
      if (field.key === 'state' && typeof text === 'string' && text && !/^[A-Za-z]{2}$/.test(text)) {
        nextErrors[field.key] = 'Informe a UF com 2 letras (ex.: SP)';
      }
    });

    setErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0) return;

    // Remove campos vazios para não enviar ruído ao provider
    const params: Record<string, unknown> = {};
    Object.entries(values).forEach(([key, value]) => {
      if (value === '' || value === undefined || value === null) return;
      params[key] = value;
    });

    // Enfileira: o provider é consultado em segundo plano pelo worker
    const result = await runOperation.mutateAsync({
      connectionId,
      operation: operation.code,
      operationName: operation.name,
      params,
    });

    onOpenChange(false);
    onQueued({ consultationId: result.consultationId, operation });
  };

  if (!operation) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[520px]">
        <DialogHeader>
          <DialogTitle>{operation.name}</DialogTitle>
          <DialogDescription>
            {operation.description || 'Preencha os dados para executar a consulta.'}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          {/* Conexão utilizada (só aparece quando existe mais de uma) */}
          {activeConnections.length > 1 && (
            <div className="space-y-2">
              <Label>Conexão</Label>
              <Select value={connectionId} onValueChange={setConnectionId}>
                <SelectTrigger>
                  <SelectValue placeholder="Selecione a conexão" />
                </SelectTrigger>
                <SelectContent>
                  {activeConnections.map((connection) => (
                    <SelectItem key={connection.id} value={connection.id}>
                      {connection.name}
                      {connection.is_default ? ' (padrão)' : ''}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          {fields.length === 0 && (
            <p className="text-sm text-muted-foreground">
              Este recurso não exige parâmetros. Clique em Consultar para executar.
            </p>
          )}

          {/* Campos dinâmicos vindos do catálogo */}
          {fields.map((field) => {
            const label = field.label ?? field.key;

            if (field.type === 'boolean') {
              return (
                <div key={field.key} className="flex items-center justify-between gap-4">
                  <div className="space-y-0.5">
                    <Label className="text-sm">{label}</Label>
                    {field.help && (
                      <p className="text-xs text-muted-foreground">{field.help}</p>
                    )}
                  </div>
                  <Switch
                    checked={Boolean(values[field.key])}
                    onCheckedChange={(checked) =>
                      setValues((prev) => ({ ...prev, [field.key]: checked }))
                    }
                  />
                </div>
              );
            }

            return (
              <div key={field.key} className="space-y-2">
                <Label>
                  {label}
                  {field.required && ' *'}
                </Label>
                <Input
                  type={field.type === 'number' ? 'number' : 'text'}
                  value={String(values[field.key] ?? '')}
                  maxLength={field.type === 'number' ? undefined : 120}
                  onChange={(e) =>
                    setValues((prev) => ({ ...prev, [field.key]: e.target.value }))
                  }
                />
                {errors[field.key] ? (
                  <p className="text-xs text-destructive">{errors[field.key]}</p>
                ) : (
                  field.help && <p className="text-xs text-muted-foreground">{field.help}</p>
                )}
              </div>
            );
          })}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancelar
          </Button>
          <Button
            onClick={handleRun}
            disabled={runOperation.isPending || !connectionId}
            className="gap-2"
          >
            {runOperation.isPending ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Search className="h-4 w-4" />
            )}
            Consultar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
